import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Colour scheme (planning-app.md §4.1): `prefers-color-scheme` is followed by default and
 * written to `data-theme` on <html>, which is where the design-system dark tokens hang
 * (`tokens.css`, with the A11Y2 T7/T8 overrides). You → Appearance can pin light or dark for
 * this browser; the choice is stored locally, not on the person: a phone in the sun and a desk
 * in the evening legitimately differ.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'planning.theme';
const listeners = new Set<() => void>();
const media = typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia('(prefers-color-scheme: dark)') : null;

export function readThemeChoice(): ThemeChoice {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

export function resolveTheme(choice: ThemeChoice = readThemeChoice()): Theme {
  if (choice !== 'system') return choice;
  return media?.matches ? 'dark' : 'light';
}

export function applyTheme(choice: ThemeChoice = readThemeChoice()): Theme {
  const theme = resolveTheme(choice);
  document.documentElement.dataset.theme = theme;
  // The browser chrome (status bar of the installed app) follows the canvas colour.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = theme === 'dark' ? '#1d2125' : '#20252A';
  return theme;
}

export function setThemeChoice(choice: ThemeChoice): void {
  if (choice === 'system') window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, choice);
  applyTheme(choice);
  for (const l of listeners) l();
}

/** Called once at boot: applies the stored or device choice and follows device changes while on "system". */
export function startTheme(): void {
  applyTheme();
  media?.addEventListener('change', () => {
    if (readThemeChoice() === 'system') {
      applyTheme();
      for (const l of listeners) l();
    }
  });
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  const onMedia = () => {
    applyTheme();
    l();
  };
  media?.addEventListener('change', onMedia);
  return () => {
    listeners.delete(l);
    media?.removeEventListener('change', onMedia);
  };
}

export function useTheme(): { choice: ThemeChoice; theme: Theme; setChoice: (c: ThemeChoice) => void } {
  const choice = useSyncExternalStore(subscribe, readThemeChoice, () => 'system' as const);
  const theme = useSyncExternalStore(subscribe, () => resolveTheme(), () => 'light' as const);
  useEffect(() => {
    applyTheme();
  }, []);
  const setChoice = useCallback((c: ThemeChoice) => setThemeChoice(c), []);
  return { choice, theme, setChoice };
}
